import { memo, useState } from "react";
import { ChevronDown, ExternalLink, Layers, Sparkles, Target, AlertTriangle, HelpCircle, Contact } from "lucide-react";

type Section = { heading: string; body: string; confidence: number; sources: string[] };
type Cluster = { key: string; label: string; hits: number; activation: number; examples: string[] };
type Signal = { label: string; score: number; source: string; url?: string; reason: string };
type ContactFact = { value: string; corroboration: number; sources: string[]; platform?: string };
export type ContactFootprint = {
  emails: ContactFact[];
  phones: ContactFact[];
  addresses: ContactFact[];
  profiles: ContactFact[];
  relatives: ContactFact[];
};

export type SocialEntry = {
  platform: string;
  url: string;
  handle?: string;
  corroboration: number;
  sources: string[];
  verdict: "confirmed" | "likely";
};

export type DeepResultsData = {
  target: string;
  headline?: string;
  bottomLine?: string;
  sections: Section[];
  clusters: Cluster[];
  signals: Signal[];
  breadth: number;
  intensity: number;
  probesPlanned?: number;
  probesExecuted?: number;
  probeHits?: number;
  unresolved: string[];
  nextSteps: string[];
  contacts?: ContactFootprint | null;
  social?: SocialEntry[];
};

/**
 * Clean, reading-first presentation of a completed investigation:
 * one bottom line, then evidence-dense collapsible sections, then the
 * pattern read-out. Replaces the scattered card grid.
 */
function DeepResultsImpl({ data, onPivot }: { data: DeepResultsData; onPivot?: (t: string) => void }) {
  return (
    <div className="space-y-4">
      <section className="glass rounded-xl p-5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
          <Sparkles className="h-3 w-3" /> Bottom line
        </div>
        {data.headline && <h2 className="mt-2 font-display text-xl leading-snug">{data.headline}</h2>}
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          {data.bottomLine || "Synthesis unavailable for this run — review the sections and signals below."}
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Pattern breadth" value={`${data.breadth}%`} />
          <Stat label="Signal intensity" value={`${data.intensity}%`} />
          <Stat label="Endpoints probed" value={fmt(data.probesExecuted)} sub={data.probesPlanned ? `of ${fmt(data.probesPlanned)} planned` : undefined} />
          <Stat label="Endpoint matches" value={fmt(data.probeHits)} />
        </div>
      </section>

      {data.sections.length > 0 && (
        <section className="space-y-2">
          {data.sections.map((s, i) => (
            <SectionCard key={`${s.heading}-${i}`} section={s} defaultOpen={i < 2} />
          ))}
        </section>
      )}

      <SocialSection entries={data.social ?? []} />

      {data.contacts && <ContactSection contacts={data.contacts} />}

      {data.clusters.length > 0 && (
        <section className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
            <Layers className="h-3 w-3" /> Pattern network activation
          </div>
          <div className="mt-3 space-y-2">
            {data.clusters.map((c) => (
              <div key={c.key} className="flex items-center gap-3">
                <div className="w-52 shrink-0 truncate text-sm">{c.label}</div>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background/60">
                  <div className="h-full rounded-full bg-cyan/70" style={{ width: `${c.activation}%` }} />
                </div>
                <div className="w-24 shrink-0 text-right font-mono text-[10px] text-muted-foreground">
                  {c.activation}/100 · {c.hits}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.signals.length > 0 && (
        <section className="glass rounded-xl p-5">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
            <Target className="h-3 w-3" /> Ranked evidence signals
          </div>
          <div className="mt-3 divide-y divide-panel-border">
            {data.signals.slice(0, 24).map((s, i) => (
              <div key={`${s.source}-${i}`} className="flex items-start gap-3 py-2.5">
                <span
                  className={`mt-0.5 rounded px-1.5 py-0.5 font-mono text-[10px] ${
                    s.score > 75 ? "bg-danger/15 text-danger" : s.score > 50 ? "bg-amber/15 text-amber" : "bg-cyan/10 text-cyan"
                  }`}
                >
                  {s.score}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm">{s.source}</div>
                  <div className="text-xs leading-relaxed text-muted-foreground">{s.reason}</div>
                </div>
                {s.url && (
                  <a href={s.url} target="_blank" rel="noreferrer" className="mt-1 text-muted-foreground hover:text-foreground">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {(data.unresolved.length > 0 || data.nextSteps.length > 0) && (
        <section className="grid gap-3 md:grid-cols-2">
          {data.unresolved.length > 0 && (
            <div className="glass rounded-xl p-5">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber">
                <HelpCircle className="h-3 w-3" /> Not established
              </div>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {data.unresolved.map((u, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber" />
                    <span>{u}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {data.nextSteps.length > 0 && (
            <div className="glass rounded-xl p-5">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
                <AlertTriangle className="h-3 w-3" /> Recommended pivots
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                {data.nextSteps.map((n, i) => (
                  <li key={i}>
                    <button
                      onClick={() => onPivot?.(n)}
                      className="w-full rounded-md border border-panel-border bg-background/40 px-3 py-2 text-left text-muted-foreground transition-colors hover:bg-panel/60 hover:text-foreground"
                    >
                      {n}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function SectionCard({ section, defaultOpen }: { section: Section; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass rounded-xl">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 px-5 py-3.5 text-left">
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="min-w-0 flex-1 truncate font-display text-base">{section.heading}</span>
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground">CONF {section.confidence}%</span>
      </button>
      {open && (
        <div className="border-t border-panel-border px-5 py-4">
          <p className="whitespace-pre-line text-sm leading-relaxed">{section.body}</p>
          {section.sources.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {section.sources.slice(0, 12).map((u, i) => (
                <a
                  key={i}
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-[240px] items-center gap-1 truncate rounded border border-panel-border bg-background/40 px-2 py-1 font-mono text-[10px] text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" /> {hostOf(u)}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-md border border-panel-border bg-background/40 p-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-lg">{value}</div>
      {sub && <div className="font-mono text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function fmt(n?: number): string {
  return typeof n === "number" ? n.toLocaleString("en-US") : "—";
}

function hostOf(u: string): string {
  try {
    return new URL(u).hostname.replace(/^www\./, "");
  } catch {
    return u.slice(0, 40);
  }
}


function ContactSection({ contacts }: { contacts: ContactFootprint }) {
  const groups: { key: keyof ContactFootprint; label: string }[] = [
    { key: "emails", label: "Email addresses" },
    { key: "phones", label: "Phone numbers" },
    { key: "addresses", label: "Postal addresses" },
    { key: "profiles", label: "Social profiles" },
    { key: "relatives", label: "Possible associates" },
  ];
  const active = groups.filter((g) => (contacts[g.key] ?? []).length > 0);
  if (!active.length) return null;
  return (
    <section className="glass rounded-xl p-5">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
        <Contact className="h-3 w-3" /> Contact &amp; social footprint
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Extracted from public pages where the target name appears alongside the value. Corroboration counts
        independent sources — a single source is an unverified candidate, not a confirmed fact.
      </p>
      <div className="mt-4 space-y-4">
        {active.map((g) => (
          <div key={g.key}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{g.label}</div>
            <ul className="mt-2 space-y-1.5">
              {(contacts[g.key] ?? []).slice(0, 12).map((f) => (
                <li key={f.value} className="flex flex-wrap items-center gap-2 text-sm">
                  {f.value.startsWith("http") ? (
                    <a href={f.value} target="_blank" rel="noreferrer" className="break-all text-cyan hover:underline">
                      {f.value}
                    </a>
                  ) : (
                    <span className="break-all">{f.value}</span>
                  )}
                  {f.platform && (
                    <span className="rounded bg-background/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {f.platform}
                    </span>
                  )}
                  <span
                    className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${
                      f.corroboration > 1 ? "bg-cyan/15 text-cyan" : "bg-background/60 text-muted-foreground"
                    }`}
                  >
                    {f.corroboration > 1 ? `${f.corroboration} sources` : "1 source · unverified"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Always rendered for a scan: an explicit verdict per platform beats a section
 * that silently disappears when nothing matched.
 */
function SocialSection({ entries }: { entries: SocialEntry[] }) {
  const confirmed = entries.filter((e) => e.verdict === "confirmed");
  const likely = entries.filter((e) => e.verdict === "likely");
  return (
    <section className="glass rounded-xl p-5">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
        <Contact className="h-3 w-3" /> Social profiles
      </div>
      {entries.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No public profile resolved to this target on this run. That is a result, not an error — either the
          accounts are private, use a different handle, or the platform blocks unauthenticated lookups.
        </p>
      ) : (
        <>
          <p className="mt-2 text-xs text-muted-foreground">
            {confirmed.length} confirmed · {likely.length} likely. Confirmed means two or more independent
            sources produced the same profile.
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {[...confirmed, ...likely].slice(0, 24).map((e) => (
              <a
                key={`${e.platform}-${e.url}`}
                href={e.url}
                target="_blank"
                rel="noreferrer noopener"
                className="flex items-center gap-2 rounded-md border border-panel-border bg-background/40 px-3 py-2 transition-colors hover:bg-panel/60"
              >
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {e.platform}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">{e.handle ?? hostOf(e.url)}</span>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] ${
                    e.verdict === "confirmed" ? "bg-success/15 text-success" : "bg-amber/15 text-amber"
                  }`}
                >
                  {e.verdict}
                </span>
                <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
              </a>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const DeepResults = memo(DeepResultsImpl);
